import { Button, Input, Modal } from '@orobix/orobix-fe-components'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { get } from '../../utils/api'
import { getQueryString } from '../../utils/queryString'
import { getObservationsQueryParams } from '../Observations/getObservationsQueryParams'
import { ObservationsParams, ObservationsResult } from '../Observations/types'
import { Observation, ProductType } from '../ObservationsSelectField/types'

// Product types that display annotation status tags (Annotated/Empty) instead of per-class tags.
// For these types, percentage selection must group by annotation status, not by individual class.
const ANNOTATION_STATUS_PRODUCT_TYPES: ProductType[] = [
  'segmentation',
  'classification_patch',
]

interface SelectPercentageButtonModalProps {
  percentage: number
  setPercentage: React.Dispatch<React.SetStateAction<number>>
  selected: Observation[]
  setSelected: (selected: Observation[]) => void
  params: ObservationsParams
  productClasses: string[]
  productType: ProductType
  inTraining: boolean
  inTest: boolean
  disabled?: boolean
}

const SelectPercentageButtonModal = ({
  percentage,
  setPercentage,
  selected,
  setSelected,
  params,
  productClasses,
  productType,
  inTraining,
  inTest,
  disabled = false,
}: SelectPercentageButtonModalProps) => {
  const { t } = useTranslation()
  const MIN = 0
  const MAX = 100
  const [isLoading, setLoading] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [tmpPercentage, setTmpPercentage] = useState<number>(percentage)

  const results = (response: ObservationsResult): Observation[] =>
    response?.results || []

  interface IGroupedObservations {
    clazz: string
    count: number
    observations?: Observation[]
    annotationClasses: string
  }

  enum EAnnotationType {
    DEFINED,
    UNDEFINED,
    DEFINED_EMPTY,
  }

  function groupAnnotationsPerType(
    countedObs: number,
    percentageObsToSelect: number,
    annotationClass: string,
    groupedObs: IGroupedObservations[],
    annotationType: EAnnotationType
  ) {
    groupedObs.push({
      clazz: annotationClass,
      count: Math.ceil((countedObs * percentageObsToSelect) / 100),
      annotationClasses: annotationType.toString(),
    })
  }

  function randomArray<T>(array: T[]): T[] {
    return array
      .map((item) => ({ item, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ item }) => item)
  }

  function getResultsFilteredByPercentage(
    // Count number of observations for each class and store number of
    // observations to be set, due to percentage needed
    _classes: string[],
    _percentage: number,
    _observations: Observation[]
  ) {
    // Concat observations to be selected
    let observationz: Observation[] = []

    // ANNOTATED => annotation_classes='DEFINED'
    // For segmentation/classification_patch, the green labels shown in the UI are
    // "Annotated" and "Empty" (annotation status), not individual product classes.
    // A single image can have multiple class annotations, so grouping per class would
    // count/select the same image multiple times. Instead, group all DEFINED images as
    // one single bucket. For anomaly/classification, keep the per-class grouping.
    if (ANNOTATION_STATUS_PRODUCT_TYPES.includes(productType)) {
      const defined = _observations.filter(
        (obs) => obs.annotationClasses === 'DEFINED'
      )
      if (defined.length > 0) {
        const count = Math.ceil((defined.length * _percentage) / 100)
        observationz = observationz.concat(randomArray(defined).slice(0, count))
      }
    } else {
      const groupedAnnotatedObservations: IGroupedObservations[] = []
      _classes.forEach((cls) => {
        const annotated = _observations.filter((obs) =>
          obs.productClasses.includes(cls)
        ).length
        if (annotated > 0) {
          groupAnnotationsPerType(
            annotated,
            _percentage,
            cls,
            groupedAnnotatedObservations,
            EAnnotationType.DEFINED
          )
        }
      })
      groupedAnnotatedObservations.forEach((ob) => {
        const obz: Observation[] = randomArray(
          _observations.filter((obs) => obs.productClasses.includes(ob.clazz))
        )
        observationz = observationz.concat(obz.slice(0, ob.count))
      })
    }

    // NOT ANNOTATED: annotation_classes='UNDEFINED'
    if (!inTraining) {
      const groupedNotAnnotatedObservations: IGroupedObservations[] = []
      const notAnnotated = _observations.filter(
        (obs) => obs.annotationClasses === 'UNDEFINED'
      ).length
      if (notAnnotated > 0) {
        groupAnnotationsPerType(
          notAnnotated,
          _percentage,
          '',
          groupedNotAnnotatedObservations,
          EAnnotationType.UNDEFINED
        )
      }
      groupedNotAnnotatedObservations.forEach((ob) => {
        const obz: Observation[] = randomArray(
          _observations.filter((obs) => obs.annotationClasses === 'UNDEFINED')
        )
        observationz = observationz.concat(obz.slice(0, ob.count))
      })
    }

    // EMPTY ANNOTATED: annotation_classes='DEFINED_EMPTY'
    const groupedEmptyAnnotatedObservations: IGroupedObservations[] = []
    const emptyAnnotated = _observations.filter(
      (obs) => obs.annotationClasses === 'DEFINED_EMPTY'
    ).length
    if (emptyAnnotated > 0) {
      groupAnnotationsPerType(
        emptyAnnotated,
        _percentage,
        '',
        groupedEmptyAnnotatedObservations,
        EAnnotationType.DEFINED_EMPTY
      )
    }
    groupedEmptyAnnotatedObservations.forEach((ob) => {
      const obz: Observation[] = randomArray(
        _observations.filter((obs) => obs.annotationClasses === 'DEFINED_EMPTY')
      )
      observationz = observationz.concat(obz.slice(0, ob.count))
    })
    return observationz
  }

  const fetchMinimalObservations = (_percentage: number) => {
    setLoading(true)
    return get<ObservationsResult>(
      `/observations/?${getQueryString({
        ...getObservationsQueryParams(params), // requires same params encoding as main /observations endpoint
        offset: 0,
        limit: 20000, // limit is required
        mode: 'no_image_data', // lightweight version of the response payload
      })}`
    )
      .then(results)
      .then((res) => {
        const observations: Observation[] = getResultsFilteredByPercentage(
          productClasses,
          _percentage,
          res
        )
        setSelected(observations)
      })
      .finally(() => setLoading(false))
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label={`${t('Select %')} ${percentage > MIN ? `(${percentage}%)` : ''}`}
        title={t('Select images by percentage of each class')}
        buttonType="button"
        type="default"
        loading={isLoading}
        disabled={disabled}
      />
      {isOpen && (
        <Modal
          size="sm"
          onCloseRequest={() => setOpen(false)}
          title={t('Images selection')}
          footer={
            <Button
              disabled={tmpPercentage < MIN || tmpPercentage > MAX}
              onClick={() => {
                if (tmpPercentage) {
                  setPercentage(tmpPercentage)
                  fetchMinimalObservations(tmpPercentage)
                } else {
                  setPercentage(0)
                }
                setOpen(false)
              }}
              label={t('Save')}
              type="action"
              buttonType="button"
            />
          }
        >
          <div>
            <Input
              name="selectByPercentage"
              label={t('Images percentage per each class')}
              id="selectByPercentage"
              type="number"
              step={1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTmpPercentage(Number(e.target.value))
              }
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                e.target.select()
              }}
              value={tmpPercentage}
              min={MIN}
              max={MAX}
              title={t(
                'Value must be between {{ minPercentage }} and {{ maxPercentage }}',
                { minPercentage: MIN, maxPercentage: MAX }
              )}
            />
          </div>
        </Modal>
      )}
    </>
  )
}

export default SelectPercentageButtonModal
